import {useState} from 'react';
import {css} from '@emotion/css';
import {colors} from 'patient-ping-remedy/packages/theme';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import IconButton from 'patient-ping-remedy/packages/icon-button';
import ToolTip from 'patient-ping-remedy/packages/tool-tip';
import ContextMenu from 'patient-ping-remedy/packages/context-menu';
import Badge from 'patient-ping-remedy/packages/badge';
import Modal from 'patient-ping-remedy/packages/modal';
import {StandardSemiBoldTypography, StandardTypography} from 'patient-ping-remedy/packages/typography';
import SortableTable, {TableData} from '@common/SortableTable';
import Loading from '@common/Loading';
import {CampaignConfigResponse} from '@/api/dto/connect';
import CopyButton from '@/components/common/CopyButton';
import {
  ActionCell,
  CampaignIdCell,
  CampaignIdText,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateTitle,
  EmptyStateWrapper,
} from '@/components/pages/settings/telephony/CampaignConfigTable.styles';

type Props = {
  campaignConfigs: CampaignConfigResponse[];
  isLoading: boolean;
  error: Error | null;
  onEdit: (config: CampaignConfigResponse) => void;
  onDeactivate: (id: number) => void;
  isDeactivating: boolean;
  onAddClick: () => void;
}

const tableHeaders = ['Group Name', 'Campaign ID', 'Voicemail Config', 'Active', ''];

function truncateCampaignId(value: string, maxLength = 24): string {
  if (value.length <= maxLength) return value;
  return `${value.substring(0, maxLength)}…`;
}

export default function CampaignConfigTable({
  campaignConfigs,
  isLoading,
  error,
  onEdit,
  onDeactivate,
  isDeactivating,
  onAddClick,
}: Props) {
  const [deactivateTarget, setDeactivateTarget] = useState<{
    id: number;
    groupName: string;
    campaignId: string;
  } | null>(null);

  const closeDeactivateModal = () => setDeactivateTarget(null);
  const confirmDeactivate = () => {
    if (deactivateTarget) {
      onDeactivate(deactivateTarget.id);
      closeDeactivateModal();
    }
  };

  const tableData: TableData[] = campaignConfigs.map((config) => ({
    groupName: {
      tableHeader: 'Group Name',
      key: 'groupName',
      type: 'string',
      value: config.rosterName,
      displayValue: config.rosterName,
      columnWidth: '35%',
    },
    campaignId: {
      tableHeader: 'Campaign ID',
      key: 'campaignId',
      type: 'string',
      value: config.campaignId,
      displayValue: (
        <CampaignIdCell>
          <ToolTip text={config.campaignId} position="top" type="dark">
            <CampaignIdText>{truncateCampaignId(config.campaignId)}</CampaignIdText>
          </ToolTip>
          <CopyButton
            value={config.campaignId}
            ariaLabel="Copy campaign ID"
            errorMessage="Unable to copy campaign ID. Your browser may not support copying."
          />
        </CampaignIdCell>
      ),
      columnWidth: '20%',
    },
    voicemailConfig: {
      tableHeader: 'Voicemail Config',
      key: 'voicemailConfig',
      type: 'string',
      value: config.voicemailConfig?.name ?? 'None',
      displayValue: config.voicemailConfig ? (
        <Badge backgroundColor={colors.blue1} textColor={colors.blue5}>
          {config.voicemailConfig.name}
        </Badge>
      ) : (
        <Badge backgroundColor={colors.gray1} textColor={colors.charcoal}>None</Badge>
      ),
      columnWidth: '20%',
    },
    active: {
      tableHeader: 'Active',
      key: 'active',
      type: 'string',
      value: config.active ? 'Active' : 'Inactive',
      displayValue: config.active ? (
        <Badge backgroundColor={colors.green1} textColor={colors.green5}>
          Active
        </Badge>
      ) : (
        <Badge backgroundColor={colors.gray1} textColor={colors.charcoal}>
          Inactive
        </Badge>
      ),
      columnWidth: '15%',
    },
    actions: {
      tableHeader: '',
      key: 'actions',
      type: 'custom',
      value: '',
      columnWidth: '10%',
      displayValue: (
        <ActionCell>
          <ContextMenu
            menuItems={[
              {
                action: () => onEdit(config),
                label: 'Edit',
              },
              ...(config.active
                ? [{
                  action: () => setDeactivateTarget({
                    id: config.id,
                    groupName: config.rosterName,
                    campaignId: config.campaignId,
                  }),
                  label: 'Deactivate',
                }]
                : []),
            ]}
            position="bottom"
            menuTestId="campaign-config-context-menu"
            triggerTestId="campaign-config-context-menu"
          >
            <IconButton iconClass={ICONS['ellipsis-h']} styleType={StyleType.TERTIARY} />
          </ContextMenu>
        </ActionCell>
      ),
    },
  }));

  return (
    <>
      {isLoading && <Loading />}
      {!isLoading && !error && campaignConfigs.length === 0 && (
        <EmptyStateWrapper>
          <Icon
            iconClass={ICONS.phone}
            iconSize="2x"
            color={colors.gray4}
          />
          <EmptyStateTitle>No campaign configs yet</EmptyStateTitle>
          <EmptyStateBody>
            Each campaign config maps a group to a campaign created in the AWS Connect console.
            Create a campaign in Connect first, then add it here so patients in that group are
            enrolled automatically.
          </EmptyStateBody>
          <EmptyStateActions>
            <Button onClick={onAddClick}>
              <Icon iconClass={ICONS.plus} color={colors.white} style={{ marginRight: '10px' }} />
              Add Campaign Config
            </Button>
          </EmptyStateActions>
        </EmptyStateWrapper>
      )}
      {(error || campaignConfigs.length > 0) && (
        <SortableTable
          id="campaign-configs-table"
          data={tableData}
          loading={false}
          loadError={error}
          header={tableHeaders}
          sortableColumns={tableData.length > 0 ? [
            tableData[0]['groupName'],
          ] : []}
          searchColumns={['groupName', 'campaignId']}
          searchPlaceholder="Search by group or campaign ID"
        >
          <Button onClick={onAddClick}>
            <Icon iconClass={ICONS.plus} color={colors.white} style={{ marginRight: '10px' }} />
            Add Campaign Config
          </Button>
        </SortableTable>
      )}
      <Modal
        isOpen={!!deactivateTarget}
        hideModal={closeDeactivateModal}
        headerText="Deactivate campaign config"
        className={css({ width: '70rem' })}
        buttons={[
          <Button
            key="cancel"
            styleType={StyleType.TERTIARY}
            onClick={closeDeactivateModal}
          >
            Cancel
          </Button>,
          <Button
            key="confirm"
            onClick={confirmDeactivate}
            disabled={isDeactivating}
          >
            Confirm
          </Button>,
        ]}
      >
        <StandardTypography>
          Are you sure you want to deactivate the campaign config for{' '}
          <StandardSemiBoldTypography>
            {deactivateTarget?.groupName}
          </StandardSemiBoldTypography>
          ?
        </StandardTypography>
        <p className={css({ marginTop: '1.2rem', color: colors.red5 })}>
          <Icon
            iconClass={ICONS['exclamation-triangle']}
            color={colors.red5}
            className={css({ marginRight: '0.6rem' })}
          />
          Patients in this group will no longer be enrolled in the campaign.
          You can reactivate this config later by adding a new mapping for the same group.
        </p>
      </Modal>
    </>
  );
}
